// MerchantDetails.jsx
import { useEffect, useState } from "react";
import {
  Row,
  Col,
  FormGroup,
  Label,
  Input,
  FormFeedback,
  Card,
  CardBody,
  InputGroup,
  InputGroupText,
  Alert,
} from "reactstrap";
import api from "../api";
import Select from "react-select";
import { Eye, EyeOff } from "lucide-react";

export default function MerchantDetails({ formik }) {
  const { values, errors, touched, handleChange, handleBlur, setFieldValue } = formik;
  const [merchantIds, setMerchantIds] = useState([]);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const encoded = localStorage.getItem("role");
  const role = atob(encoded);

  useEffect(() => {
    async function fetchMerchantIds() {
      try {
        const token = localStorage.getItem("jwt");
        const res = await api.post(
          "/merchants/getMerchantId",
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const ids = Array.isArray(res.data)
          ? res.data.map((item) => (typeof item === "string" ? item : item.id))
          : [];

        setMerchantIds(ids);

        if ((role === "MERCHANT" || role === "VIEWER") && ids.length > 0) {
          setFieldValue("merchantId", ids[0]);
        } else if (ids.length > 0 && !values.merchantId) {
          setFieldValue("merchantId", ids[0]);
        }
      } catch (err) {
        setError(err.message || "Failed to load merchant IDs");
      }
    }
    fetchMerchantIds();
  }, [values.merchantId, setFieldValue, role]);

  return (
    <>
      {/* Merchant ID */}
      <Row>
        <Col md="6">
          <FormGroup>
            <Label className="form-label">Merchant ID *</Label>
            <Select
              name="merchantId"
              value={
                values.merchantId
                  ? { value: values.merchantId, label: values.merchantId }
                  : null
              }
              onChange={(option) => setFieldValue("merchantId", option.value)}
              onBlur={handleBlur}
              options={merchantIds.map((id) => ({ value: id, label: id }))}
              classNamePrefix="react-select"
              className={touched.merchantId && errors.merchantId ? "is-invalid" : ""}
              isDisabled={role === "MERCHANT" || role === "VIEWER"}
            />
            <FormFeedback>{touched.merchantId && errors.merchantId}</FormFeedback>
          </FormGroup>
        </Col>
      </Row>

      {/* Credentials Card */}
      <Card className="mt-3 shadow-sm border-0">
        <CardBody>
          <Row>
            <Col md="12">
              <small className="text-muted d-block mb-3 info-note">
                Note: The merchant credentials entered here are created specifically
                for this dashboard, which provides access to view and manage all related transactions.
              </small>
            </Col>

            {/* Username */}
            <Col md="6">
              <FormGroup>
                <Label className="form-label">Merchant Username *</Label>
                <Input
                  name="username"
                  value={values.username || ""}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  invalid={touched.username && !!errors.username}
                  placeholder="Enter username"
                  autoComplete="new-username"
                />
                <FormFeedback>{touched.username && errors.username}</FormFeedback>
              </FormGroup>
            </Col>

            {/* Password with toggle */}
            <Col md="6">
              <FormGroup>
                <Label className="form-label">Merchant Password *</Label>
                <InputGroup>
                  <Input
                    name="password"
                    type={showPassword ? "text" : "password"}
                    value={values.password || ""}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    invalid={touched.password && !!errors.password}
                    placeholder="Enter password"
                    autoComplete="new-password"
                  />
                  <InputGroupText
                    role="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="toggle-btn"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </InputGroupText>
                </InputGroup>
                <FormFeedback>{touched.password && errors.password}</FormFeedback>
              </FormGroup>
            </Col>
          </Row>
        </CardBody>
      </Card>

      {error && <Alert color="danger" className="mt-3">{error}</Alert>}
    </>
  );
}
